import { useState, useEffect } from 'react';
import { hospitalApi } from '../../hospitalApi';

const C = '#1b6ca8';
const DARK = '#0f4c75';
const LIGHT = '#e8f4fd';

const fmtRs = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_COLOR = {
  scheduled: '#1b6ca8',
  completed: '#27ae60',
  cancelled: '#c0392b',
  'no-show': '#e67e22',
};

export default function HospitalDashboard({ onNavigate, role }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    hospitalApi.getDashboard().then(d => {
      if (d) setData(d);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <div style={{ fontSize: 15, color: '#5f6368' }}>Loading dashboard…</div>
      </div>
    );
  }

  const stats = [
    { label: "Today's Appointments", value: data?.todayAppointments ?? 0, icon: '📅', color: C, nav: 'appointments' },
    { label: 'Monthly Collection', value: fmtRs(data?.monthlyCollection), icon: '💰', color: '#27ae60', nav: 'billing' },
    { label: 'Monthly Expenses', value: fmtRs(data?.monthlyExpenses), icon: '📊', color: '#e67e22', nav: 'expenses' },
    { label: 'Total Patients', value: data?.totalPatients ?? 0, icon: '👥', color: DARK, nav: 'patients' },
    { label: 'Pending Bills', value: data?.pendingInvoices ?? 0, icon: '⏳', color: '#c0392b', nav: 'billing' },
    { label: 'Active Doctors', value: data?.doctorCount ?? 0, icon: '👨‍⚕️', color: '#8e44ad', nav: 'doctors' },
  ];

  const todayList = data?.todayAppointmentList || [];
  const expBreakdown = data?.expenseBreakdown || [];
  const maxExp = expBreakdown.length > 0 ? Math.max(...expBreakdown.map(e => Number(e.total || 0))) : 1;

  return (
    <div>
      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14, marginBottom: 24 }}>
        {stats.map((s, i) => (
          <div key={i}
            onClick={() => onNavigate && onNavigate(s.nav)}
            style={{ background: '#fff', borderRadius: 12, padding: '18px 20px',
              boxShadow: '0 1px 6px rgba(0,0,0,.08)', cursor: 'pointer',
              borderTop: `4px solid ${s.color}`, transition: 'transform .15s, box-shadow .15s' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,.13)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 1px 6px rgba(0,0,0,.08)'; }}
          >
            <div style={{ fontSize: 28, marginBottom: 8 }}>{s.icon}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 12, color: '#5f6368', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Two columns below */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Today's Appointments */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">📅 Today's Appointments</div>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {todayList.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#9aa0a6', fontSize: 14 }}>
                No appointments scheduled for today
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: LIGHT }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', color: DARK, fontWeight: 700 }}>Patient</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', color: DARK, fontWeight: 700 }}>Doctor</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', color: DARK, fontWeight: 700 }}>Time</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', color: DARK, fontWeight: 700 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {todayList.map((a, i) => (
                    <tr key={a.id} style={{ borderTop: '1px solid #f1f3f4', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                      <td style={{ padding: '8px 12px' }}>{a.patient_name || '—'}</td>
                      <td style={{ padding: '8px 12px', color: '#5f6368' }}>{a.doctor_name || '—'}</td>
                      <td style={{ padding: '8px 12px' }}>{a.appt_time || '—'}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{
                          background: STATUS_COLOR[a.status] + '20',
                          color: STATUS_COLOR[a.status] || '#5f6368',
                          borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600
                        }}>
                          {a.status || 'scheduled'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Expense Breakdown */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">📊 Monthly Expense Breakdown</div>
          </div>
          <div className="card-body">
            {expBreakdown.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#9aa0a6', fontSize: 14 }}>No expenses recorded this month</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {expBreakdown.map((e, i) => {
                  const pct = Math.round((Number(e.total || 0) / maxExp) * 100);
                  return (
                    <div key={i}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 3 }}>
                        <span style={{ fontWeight: 600, color: '#202124' }}>{e.category}</span>
                        <span style={{ fontWeight: 700, color: DARK }}>{fmtRs(e.total)}</span>
                      </div>
                      <div style={{ height: 8, background: '#e8eaed', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: pct + '%', background: `linear-gradient(90deg, ${C}, #3498db)`, borderRadius: 4 }} />
                      </div>
                    </div>
                  );
                })}
                <div style={{ borderTop: '1px solid #e8eaed', paddingTop: 10, marginTop: 4,
                  display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 14, color: DARK }}>
                  <span>Total Expenses</span>
                  <span>{fmtRs(data?.monthlyExpenses)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
